<template>
  <div class="amount-with-unit-wrapper">
    <div class="field has-addons">
      <div class="control">
        <input
          :id="inputId"
          :key="`amount-${uid}`"
          :value="amount"
          type="number"
          inputmode="decimal"
          min="0"
          step="any"
          autocomplete="off"
          class="input is-small amount-input"
          :class="{ 'is-danger': validationError }"
          :style="inputStyle"
          :placeholder="placeholder"
          data-test-id="amount-input"
          @input="handleAmountInput($event.target.value)"
          @blur="handleBlur"
          @keydown.enter.stop.prevent="$event.target.blur()"
          @keydown="preventScientificNotation"
        />
      </div>
      <p v-if="!hideUnit && lockedUnit" class="control">
        <span
          class="button is-static is-small dietary-unit"
          :class="{ 'dietary-unit--stacked': ['mcg RAE', 'mcg DFE'].includes(unit || defaultUnit) }"
        >
          <template v-if="(unit || defaultUnit) === 'mcg RAE'">
            <span class="unit-part">mcg</span>
            <span class="unit-part">RAE</span>
          </template>
          <template v-else-if="(unit || defaultUnit) === 'mcg DFE'">
            <span class="unit-part">mcg</span>
            <span class="unit-part">DFE</span>
          </template>
          <template v-else>
            {{ unit || defaultUnit }}
          </template>
        </span>
      </p>
      <div v-else-if="!hideUnit" class="control">
        <UnitDropdown
          :key="`unit-${uid}`"
          :model-value="unit"
          :allowed-units="allowedUnits"
          :placeholder="unitPlaceholder"
          @update:model-value="$emit('update:unit', $event)"
        />
      </div>
    </div>
    <p v-if="validationError" class="help is-danger">{{ validationError }}</p>
  </div>
</template>

<script setup>
  import { computed, ref } from "vue";
  import UnitDropdown from "./unit-dropdown.vue";
  import { preventScientificNotation } from "./utils/formatting";

  const props = defineProps({
    amount: { type: [String, Number], default: "" },
    unit: { type: String, default: "" },
    placeholder: { type: String, default: "e.g., 500" },
    unitPlaceholder: { type: String, default: "mg" },
    lockedUnit: { type: Boolean, default: false },
    defaultUnit: { type: String, default: "mg" },
    uid: { type: [String, Number], default: "" },
    hideUnit: { type: Boolean, default: false },
    allowedUnits: { type: Array, default: null },
    inputId: { type: String, default: "" },
  });

  const emit = defineEmits(["update:amount", "update:unit", "blur"]);

  const validationError = ref(null);

  // Reactive width: expand input if amount string exceeds base width threshold
  const inputStyle = computed(() => {
    const baseThreshold = 7; // characters before we need to grow
    const maxWidth = 15; // max characters to prevent extreme widths
    const amountStr = String(props.amount ?? "");
    if (amountStr.length > baseThreshold) {
      // Add 2ch buffer for padding/cursor, capped at maxWidth
      const width = Math.min(amountStr.length + 2, maxWidth);
      return { width: `${width}ch` };
    }
    return {};
  });

  function handleAmountInput(value) {
    const numValue = parseFloat(value);
    if (value === "" || value === null) {
      validationError.value = null;
      emit("update:amount", "");
    } else if (!isNaN(numValue) && numValue >= 0) {
      validationError.value = null;
      emit("update:amount", value);
    } else {
      validationError.value = "Enter a positive number";
    }
  }

  function handleBlur() {
    // Normalize trailing decimal points (e.g., "1." → "1") before save
    if (typeof props.amount === "string" && props.amount.endsWith(".")) {
      emit("update:amount", props.amount.slice(0, -1));
    }
    validationError.value = null;
    emit("blur");
  }
</script>

<style lang="scss" scoped>
  $field-height: 28px;

  .amount-with-unit-wrapper {
    display: inline-block;

    .help.is-danger {
      font-size: 0.7rem;
      margin-top: 0.15rem;
    }
  }

  .field.has-addons {
    .control {
      .amount-input {
        height: $field-height !important;
        min-height: $field-height !important;
        max-height: $field-height !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: $field-height !important;
        box-sizing: border-box !important;
      }

      :deep(.unit-dropdown-button) {
        height: $field-height !important;
        min-height: $field-height !important;
        max-height: $field-height !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: $field-height !important;
        box-sizing: border-box !important;
      }
    }
  }

  .dietary-unit {
    height: $field-height !important;
    min-height: $field-height !important;
    max-height: $field-height !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: $field-height !important;
    box-sizing: border-box !important;
  }

  .dietary-unit--stacked {
    flex-direction: column !important;
    line-height: 1 !important;
    padding-left: 0.5em !important;
    padding-right: 0.5em !important;
    min-width: 3.5em;

    .unit-part {
      font-size: 12px;
      line-height: 1.1;
      font-weight: 500;
    }
  }
</style>
